import { useId } from 'react';

import { Box, Heading, Icon, Paragraph, Tag, TextButton, ToggleCard } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconLightningFilled } from '@zillow/constellation-icons';

import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';

import { useCanAccessCondensedFlow } from '../../../../../hooks';

import {
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../../styles/wizard';

export type TourSchedulingBookingMethodBodyProps = {
  selectedBookingMethod?: TourConfirmationType;
  onSelectBookingMethod: (bookingMethod: TourConfirmationType | undefined) => void;
};

const TourSchedulingBookingMethodBody = ({
  selectedBookingMethod,
  onSelectBookingMethod,
}: TourSchedulingBookingMethodBodyProps) => {
  const canAccessCondensedFlow = useCanAccessCondensedFlow();

  const bookingMethodHeadingId = useId();

  return (
    <div data-testid="TourSchedulingBookingMethodBody">
      <Box css={wizardFormSectionContainerStyles.raw()}>
        <Heading
          id={bookingMethodHeadingId}
          level={1}
          textStyle={canAccessCondensedFlow ? 'heading-md' : 'heading-sm'}
          css={wizardHeadingStyles}
          data-testid="TourSchedulingBookingMethodBody-Heading"
        >
          Do you want to use Zillow to book tours?
        </Heading>
        <Paragraph css={wizardParagraphStyles} data-testid="TourSchedulingBookingMethodBody-Description">
          By adding availability to a tour calendar, renters can find a time that you’re free and save you the back and
          forth. Select a booking method to use Zillow, or select nothing and press “Next” to manage tour requests on
          your own.
        </Paragraph>

        <Box
          role="group"
          aria-labelledby={bookingMethodHeadingId}
          css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'default' }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' }, paddingLeft: 'none' }}>
            <ToggleCard
              selected={selectedBookingMethod === TourConfirmationType.INSTANT}
              onClick={() =>
                onSelectBookingMethod(
                  selectedBookingMethod === TourConfirmationType.INSTANT ? undefined : TourConfirmationType.INSTANT,
                )
              }
              elevated={false}
              data-testid="TourSchedulingBookingMethodBody-BookingMethodCard-BookInstantly"
              tone={selectedBookingMethod === TourConfirmationType.INSTANT ? 'soft' : 'neutral'}
              css={{ height: '100%' }}
            >
              <Box css={{ margin: 'tighter' }}>
                <Icon
                  size="lg"
                  css={{
                    marginBottom: 'loose',
                    color:
                      selectedBookingMethod === TourConfirmationType.INSTANT ? 'icon.accent.brand.hero' : 'icon.subtle',
                  }}
                >
                  <IconLightningFilled />
                </Icon>
                <Box css={{ marginBottom: 'tighter' }}>
                  <Heading level={2} textStyle="heading-xs">
                    Book tours instantly
                  </Heading>
                </Box>
                <Tag tone="purple" size="sm" css={{ marginBottom: 'loose' }}>
                  Most efficient
                </Tag>
                <Paragraph css={{ marginBottom: 'tighter' }}>
                  Renters directly book a time from the availability provided on your listing.
                </Paragraph>
              </Box>
            </ToggleCard>
          </Box>
          <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' }, paddingLeft: 'none' }}>
            <ToggleCard
              selected={selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM}
              onClick={() =>
                onSelectBookingMethod(
                  selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM
                    ? undefined
                    : TourConfirmationType.LANDLORD_CONFIRM,
                )
              }
              elevated={false}
              data-testid="TourSchedulingBookingMethodBody-BookingMethodCard-ReviewAndConfirm"
              tone={selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM ? 'soft' : 'neutral'}
              css={{ height: '100%' }}
            >
              <Box css={{ margin: 'tighter' }}>
                <Icon
                  size="lg"
                  css={{
                    marginBottom: 'loose',
                    color:
                      selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM
                        ? 'icon.accent.brand.hero'
                        : 'icon.subtle',
                  }}
                >
                  <IconCheckmarkCircleFilled />
                </Icon>
                <Box css={{ marginBottom: 'tighter' }}>
                  <Heading level={2} textStyle="heading-xs">
                    Review and confirm
                  </Heading>
                </Box>
                <Tag tone="purple" size="sm" css={{ marginBottom: 'loose' }}>
                  More control
                </Tag>
                <Paragraph>
                  Renters request a time from the availability on your listing, then you confirm or decline.
                </Paragraph>
              </Box>
            </ToggleCard>
          </Box>
        </Box>

        {selectedBookingMethod && (
          <TextButton
            data-testid="TourSchedulingBookingMethodBody-ClearButton"
            css={{ marginTop: 'loose' }}
            onClick={() => onSelectBookingMethod(undefined)}
          >
            Clear
          </TextButton>
        )}
      </Box>
    </div>
  );
};

export default TourSchedulingBookingMethodBody;
